import { ProgramEngagementModel } from '@cibo/core'
import { PROGRAM_ROUTES } from '@cibo/ui'
import Tractor from '@mui/icons-material/Agriculture'
import Cancel from '@mui/icons-material/Cancel'
import CheckCircle from '@mui/icons-material/CheckCircle'
import HourglassTop from '@mui/icons-material/HourglassTop'
import RouteOutlined from '@mui/icons-material/RouteOutlined'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useProgramEngagementContext } from '../../../context'
import { TaskMarkdown } from '../Tasks/TaskMarkdown'
import { useNavigateToNextTask } from '../useNavigateToNextTask'

type VariantType = 'success' | 'keepGoing' | 'waiting' | 'nextUp' | 'failure' | 'notFound' | 'none'

const variantIcons = {
  success: <CheckCircle color="success" fontSize="inherit" sx={{ fontSize: 60 }} />,
  keepGoing: <CheckCircle color="success" fontSize="inherit" sx={{ fontSize: 60 }} />,
  waiting: (
    <HourglassTop
      color="warning"
      fontSize="inherit"
      sx={{
        fontSize: 48,
        backgroundColor: 'warning.main',
        color: 'warning.contrastText',
        borderRadius: '50%',
        padding: 0.7,
      }}
    />
  ),
  nextUp: (
    <RouteOutlined
      color="success"
      fontSize="inherit"
      sx={{
        fontSize: 48,
        backgroundColor: 'success.main',
        color: 'success.contrastText',
        borderRadius: '50%',
        padding: 0.7,
      }}
    />
  ),
  failure: <Cancel color="error" fontSize="inherit" sx={{ fontSize: 48 }} />,
  notFound: <Tractor color="error" fontSize="inherit" sx={{ fontSize: 48 }} />,
  none: null,
}

type InterstitialDisplayProps = {
  markdown?: string
  variant?: VariantType
  cta?: string | false
  header?: string
  taskId?: string
  engagement?: ProgramEngagementModel
}

export const InterstitialDisplay = (props: InterstitialDisplayProps) => {
  const { t } = useTranslation('@cibo/programs/InterstitialDisplay')
  const { engagement } = useProgramEngagementContext()

  const navigateToNextTask = useNavigateToNextTask({ fallbackDestination: PROGRAM_ROUTES.ROOT })

  const variantHeader: Record<string, any> = useMemo(
    () => ({
      success: t('applicationAccepted'),
      keepGoing: t('keepGoing'),
      waiting: t('waiting'),
      nextUp: t('nextUp'),
      failure: t('applicationRejected'),
      notFound: t('notFound'),
    }),
    [t]
  )

  const cta = props?.cta
  const variant = props?.variant || 'notFound'
  const markdown = props?.markdown
  const headerContent = props?.header || variantHeader[variant]

  return (
    <Stack alignItems="center" textAlign="center" flex={1} spacing={3}>
      <Stack alignItems="center" textAlign="center" flex={1} spacing={1.5}>
        {!!variantIcons[variant] && (
          <Stack alignItems="center" textAlign="center" spacing={2}>
            {variantIcons[variant]}
            <Typography variant="overline">{headerContent}</Typography>
          </Stack>
        )}

        <Box maxWidth={600}>
          <TaskMarkdown color="text.secondary">
            {markdown && engagement.data?.replaceCopyTokens(markdown)}
          </TaskMarkdown>
        </Box>
      </Stack>

      {cta !== false && (
        <Button
          data-testid="continueBtn"
          onClick={() =>
            props.engagement &&
            navigateToNextTask({ taskId: props.taskId, engagement: props.engagement })
          }
          variant="contained"
        >
          {cta ?? t('continue')}
        </Button>
      )}
    </Stack>
  )
}
